import React, { useState, useEffect } from 'react';
import { Layout, Menu, Badge, Dropdown, Avatar, Button, List, Typography, Space, message, Drawer, Descriptions, Tag, Tabs, Modal, Grid, notification } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BulbOutlined,
  TranslationOutlined,
  CalculatorOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  CheckOutlined,
  HistoryOutlined,
  MailOutlined,
  TeamOutlined,
  MenuOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { orderApi, emailApi, rebillApi } from '../services/api';
import { fmtCN } from '../utils/time';
import { actionText, fieldText } from '../utils/logLabels';
import { parseContent, logDeltaText, cleanLogRemark, renderLatestSegment } from '../utils/logRender';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// 抽屉/弹窗宽度自适应：PC 用期望宽度，小屏不超出屏幕（留边距）
const respWidth = (w) => Math.min(w, (typeof window !== 'undefined' ? window.innerWidth : 1440) - 24);

const priorityColor = { urgent: 'red', high: 'orange', normal: 'blue', low: 'default' };
const priorityText = { urgent: '紧急重要', high: '一般重要', normal: '普通', low: '普通' };
// 工单状态统一中文显示
const ORDER_STATUS_COLOR = { pending: 'gold', processing: 'blue', awaiting_customer: 'purple', completed: 'green' };
const ORDER_STATUS_TEXT = { pending: '调度待跟进', processing: '调度处理中', awaiting_customer: '待客服处理', completed: '调度完成' };
const reminderCycleLabels = {
  hourly: '每小时', daily: '每天', every_2_days: '每2天', weekly: '每周', custom: '自定义提醒', none: '不提醒',
};

function OrderDetailPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [logs, setLogs] = useState([]);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyNote, setReplyNote] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  // 调度补填处理结果和过程（pending + need_result → 待客服处理）
  const [resultOpen, setResultOpen] = useState(false);
  const [resultNote, setResultNote] = useState('');
  const [resultLoading, setResultLoading] = useState(false);

  const handleReply = async () => {
    if (!replyNote.trim()) { message.warning('请填写跟进处理意见'); return; }
    setReplyLoading(true);
    try {
      await orderApi.customerReply(order.id, { reply_note: replyNote.trim() });
      message.success('已回复，工单流转回调度待跟进');
      setReplyOpen(false);
      setReplyNote('');
      setOrder({ ...order, status: 'pending', need_result: false, customer_unread: false });
      const l = await orderApi.getLogs(order.id);
      setLogs(Array.isArray(l) ? l : (l?.items || []));
    } catch (e) {
      message.error(e.message || '回复失败');
    } finally {
      setReplyLoading(false);
    }
  };

  const handleSubmitResult = async () => {
    if (!resultNote.trim()) { message.warning('请填写处理结果和过程'); return; }
    setResultLoading(true);
    try {
      await orderApi.toAwaitingCustomer(order.id, resultNote.trim());
      message.success('已流转至待客服处理工单');
      setResultOpen(false);
      setResultNote('');
      setOrder({ ...order, status: 'awaiting_customer', need_result: false, customer_unread: true });
      const l = await orderApi.getLogs(order.id);
      setLogs(Array.isArray(l) ? l : (l?.items || []));
    } catch (e) {
      message.error(e.message || '提交失败');
    } finally {
      setResultLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const m = location.pathname.match(/\/orders\/(?:[\w-]+\/)?detail\/(\d+)$/);
    if (m) {
      const id = parseInt(m[1], 10);
      (async () => {
        try {
          const [o, l] = await Promise.all([orderApi.get(id), orderApi.getLogs(id)]);
          if (cancelled) return;
          setOrder(o);
          setLogs(Array.isArray(l) ? l : (l?.items || []));
          setOpen(true);
          // 客服/销售打开待客服处理工单：蓝点置灰（后端持久化）
          if (o.status === 'awaiting_customer' && o.customer_unread) {
            orderApi.customerRead(id).catch(() => {});
            setOrder({ ...o, customer_unread: false });
          }
        } catch (e) {
          message.error(e.message || '获取订单详情失败');
        }
      })();
    } else {
      setOpen(false);
      setOrder(null);
      setLogs([]);
    }
    return () => { cancelled = true; };
  }, [location.pathname]);

  const close = () => {
    setOpen(false);
    // 返回：匹配从哪一个列表页进来的，优先回跳
    const from = sessionStorage.getItem('order_detail_back') || '/dashboard';
    sessionStorage.removeItem('order_detail_back');
    navigate(from, { replace: true });
  };

  const handleGoEdit = () => {
    const statusToPath = { pending: '/orders/pending', processing: '/orders/processing', awaiting_customer: '/orders/all?status=awaiting_customer', urgent: '/orders/urgent', completed: '/orders/completed' };
    const target = (order && statusToPath[order.status]) || '/orders/pending';
    setOpen(false);
    navigate(`${target}${target.includes('?') ? '&' : '?'}edit=${order.id}`, { replace: true });
  };

  return (
    <Drawer
      title="工单详情"
      className="order-detail-drawer"
      open={open}
      extra={order ? (
        <Space>
          {order.status === 'awaiting_customer' && (
            <Button type="primary" onClick={() => setReplyOpen(true)}>回复处理意见</Button>
          )}
          {order.status === 'pending' && order.need_result && (
            <Button type="primary" style={{ background: '#722ed1', borderColor: '#722ed1' }} onClick={() => setResultOpen(true)}>补填结果转客服</Button>
          )}
          <Button onClick={handleGoEdit}>去编辑</Button>
        </Space>
      ) : null}
      onClose={close}
      width={respWidth(1100)}
      styles={{ wrapper: { marginRight: 'calc((100vw - min(1100px, 96vw))/2)' } }}
    >
      {order && (
        <Tabs
          items={[
            {
              key: 'info',
              label: '订单信息',
              children: (
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label="订单号">{order.order_no}</Descriptions.Item>
                  <Descriptions.Item label="工单状态">
                    <Tag color={ORDER_STATUS_COLOR[order.status]}>{ORDER_STATUS_TEXT[order.status] || '调度待跟进'}</Tag>
                    {order.status === 'pending' && order.need_result && <Tag color="purple">待补处理结果</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="客户">{order.customer_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="优先级"><Tag color={priorityColor[order.priority]}>{priorityText[order.priority] || '普通'}</Tag></Descriptions.Item>
                  <Descriptions.Item label="业务类型">{(order.business_type || '').toUpperCase()}</Descriptions.Item>
                  <Descriptions.Item label="卡司">{order.carrier || '-'}</Descriptions.Item>
                  <Descriptions.Item label="卡车平台">{order.carrier_platform || '-'}</Descriptions.Item>
                  <Descriptions.Item label="提货地址">{order.pickup_location || '-'}</Descriptions.Item>
                  <Descriptions.Item label="送货地址">{order.delivery_location || '-'}</Descriptions.Item>
                  <Descriptions.Item label="收件人">{order.consignee || '-'}</Descriptions.Item>
                  <Descriptions.Item label="货物">{order.cargo_description || '-'}</Descriptions.Item>
                  <Descriptions.Item label="重量/尺寸">{order.weight || '-'} / {order.dimensions || '-'}</Descriptions.Item>
                  <Descriptions.Item label="提醒周期">{reminderCycleLabels[order.reminder_cycle] || order.reminder_cycle || '-'}</Descriptions.Item>
                  <Descriptions.Item label="创建时间">{order.created_at ? fmtCN(order.created_at) : '-'}</Descriptions.Item>
                  <Descriptions.Item label="更新时间">{order.updated_at ? fmtCN(order.updated_at) : '-'}</Descriptions.Item>
                  <Descriptions.Item label="核心问题" span={2} styles={{ label: { verticalAlign: 'top', paddingTop: 12 }, content: { minHeight: 56, verticalAlign: 'top' } }}><span style={{ whiteSpace: 'pre-wrap' }}>{renderLatestSegment(order.core_issue)}</span></Descriptions.Item>
                  <Descriptions.Item label="处理思路" span={2} labelStyle={{ verticalAlign: 'top', paddingTop: 12 }} contentStyle={{ minHeight: 56, verticalAlign: 'top' }}><span style={{ whiteSpace: 'pre-wrap' }}>{order.handling_strategy || '-'}</span></Descriptions.Item>
                  <Descriptions.Item label="跟进策略" span={2} labelStyle={{ verticalAlign: 'top', paddingTop: 12 }} contentStyle={{ minHeight: 56, verticalAlign: 'top' }}><span style={{ whiteSpace: 'pre-wrap' }}>{order.follow_up_plan || '-'}</span></Descriptions.Item>
                  <Descriptions.Item label="实际操作" span={2}><span style={{ whiteSpace: 'pre-wrap' }}>{order.actual_action || '-'}</span></Descriptions.Item>
                  <Descriptions.Item label="结果反馈" span={2}><span style={{ whiteSpace: 'pre-wrap' }}>{order.result_feedback || '-'}</span></Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'logs',
              label: (<span><HistoryOutlined /> 操作日志 ({logs.length})</span>),
              children: (
                <List
                  dataSource={logs}
                  renderItem={(log) => (
                    <List.Item>
                      <List.Item.Meta
                        title={<Space><Tag>{actionText(log.action)}</Tag>{log.field_changed && <Text type="secondary">{fieldText(log.field_changed)}</Text>}</Space>}
                        description={
                          <div>
                            {log.remark && <div style={{ whiteSpace: 'pre-wrap' }}>{parseContent(cleanLogRemark(log.remark))}</div>}
                            {(() => {
                              const delta = logDeltaText(log);
                              if (!delta) return null;
                              if (log.action === 'status_change') {
                                return <Text type="secondary" style={{ fontSize: 12 }}>→ {ORDER_STATUS_TEXT[log.new_value] || delta}</Text>;
                              }
                              return <div style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap', marginTop: 2 }}>{parseContent(delta)}</div>;
                            })()}
                            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                              <Text strong style={{ color: log.is_system ? '#999' : '#1677ff', fontSize: 11 }}>
                                操作人：{log.operator_name || '未知'}
                              </Text>
                              {'　'}{log.created_at ? fmtCN(log.created_at, 'YYYY-MM-DD HH:mm:ss') : ''}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ),
            },
          ]}
        />
      )}
      <Modal
        title="回复处理意见"
        open={replyOpen}
        onOk={handleReply}
        confirmLoading={replyLoading}
        onCancel={() => setReplyOpen(false)}
        okText="提交回复"
        cancelText="取消"
        width={520}
      >
        <p style={{ color: '#666', fontSize: 13, marginBottom: 10 }}>
          填写最新需要跟进的处理意见，提交后工单将流转回「调度待跟进工单」，由调度继续跟进。
        </p>
        <textarea
          rows={5}
          style={{ width: '100%', padding: 8, border: '1px solid #d9d9d9', borderRadius: 6, resize: 'vertical' }}
          placeholder="填写跟进处理意见（必填）"
          value={replyNote}
          onChange={(e) => setReplyNote(e.target.value)}
        />
      </Modal>
      <Modal
        title="补填处理结果和过程"
        open={resultOpen}
        onOk={handleSubmitResult}
        confirmLoading={resultLoading}
        onCancel={() => setResultOpen(false)}
        okText="提交并流转给客服"
        cancelText="取消"
        width={520}
      >
        <p style={{ color: '#666', fontSize: 13, marginBottom: 10 }}>
          该工单已匹配到邮件，补填本轮处理结果和过程后，工单将流转至「待客服处理工单」，由客服/销售查看并回复。
        </p>
        <textarea
          rows={6}
          style={{ width: '100%', padding: 8, border: '1px solid #d9d9d9', borderRadius: 6, resize: 'vertical' }}
          placeholder="填写处理结果和过程（必填）"
          value={resultNote}
          onChange={(e) => setResultNote(e.target.value)}
        />
      </Modal>
    </Drawer>
  );
}

// 跳转详情前先记住当前页面（用于 drawer 关闭回跳）
export function navigateToOrderDetail(navigateFn, orderId) {
  const cur = window.location.pathname + window.location.search;
  sessionStorage.setItem('order_detail_back', cur);
  navigateFn(`/orders/detail/${orderId}`);
}

// 前端静态配置：一级菜单的二级子路由（keyed by module_key）；一级菜单本身由后端模块配置动态下发
const MENU_CHILDREN = {
  myorders: [
    { key: '/kewell-orders', label: '科维斯订单' },
    { key: '/orders/all', label: '查看我的所有工单' },
  ],
  // 三类工单各为一个一级菜单（调度管理 / 申诉管理 / 索赔管理），下挂「待跟进 / 处理中 / 完成」三个状态子菜单，
  // 与后端模块 key 对应：orders / appeals / claims
  orders: [
    { key: '/orders/pending', label: '调度待跟进工单' },
    { key: '/orders/processing', label: '调度处理中工单' },
    { key: '/orders/completed', label: '调度完成' },
  ],
  appeals: [
    { key: '/appeals/pending', label: '申诉待跟进工单' },
    { key: '/appeals/processing', label: '申诉处理中工单' },
    { key: '/appeals/completed', label: '申诉完成' },
  ],
  claims: [
    { key: '/claims/pending', label: '索赔待跟进工单' },
    { key: '/claims/processing', label: '索赔处理中工单' },
    { key: '/claims/completed', label: '索赔完成' },
  ],
  customers: [
    { key: '/customers', label: '客户添加' },
    { key: '/priority', label: '优先级规则' },
  ],
  rebills: [
    { key: '/rebills', label: '返账单登记' },
  ],
};

// 后端模块 icon 字符串 → antd 图标组件
const MENU_ICON_MAP = {
  DashboardOutlined: <DashboardOutlined />,
  GlobalOutlined: <GlobalOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  RocketOutlined: <SyncOutlined />,
  CheckCircleOutlined: <CheckCircleOutlined />,
  MailOutlined: <MailOutlined />,
  TeamOutlined: <TeamOutlined />,
  BulbOutlined: <BulbOutlined />,
  TranslationOutlined: <TranslationOutlined />,
  DollarOutlined: <CalculatorOutlined />,
  WarningOutlined: <WarningOutlined />,
  FileTextOutlined: <FileTextOutlined />,
};

// 由模块配置动态构建 路径→module_key 映射（含子路由、深层路径前缀兜底）
function buildPathToKey(perms) {
  const map = {};
  (perms || []).forEach((m) => {
    if (m.path) map[m.path] = m.module_key;
    (MENU_CHILDREN[m.module_key] || []).forEach((c) => { map[c.key] = m.module_key; });
  });
  return map;
}

export function resolveModuleKey(path) {
  try {
    const perms = JSON.parse(localStorage.getItem('module_perms') || '[]');
    const map = buildPathToKey(perms);
    if (map[path]) return map[path];
    const hit = Object.keys(map).filter((k) => path.startsWith(k + '/')).sort((a, b) => b.length - a.length)[0];
    return hit ? map[hit] : undefined;
  } catch {
    return undefined;
  }
}

// 模块权限查询辅助：页面调用判断当前模块是否只读（从 localStorage 读取配置）
export function isModuleReadOnly(path) {
  try {
    const perms = JSON.parse(localStorage.getItem('module_perms') || '[]');
    const mk = resolveModuleKey(path);
    if (!mk) return false;
    const m = perms.find(p => p.module_key === mk);
    return m ? m.access_level === 'ro' : false;
  } catch {
    return false;
  }
}

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [reminderCount, setReminderCount] = useState(0);
  const [reminders, setReminders] = useState([]);
  // 返账单提醒（独立于订单提醒，每用户独立未读计数；「全部已读」只清订单提醒）
  const [rebillReminderCount, setRebillReminderCount] = useState(0);
  const [rebillReminders, setRebillReminders] = useState([]);
  const [rebillReminderOpen, setRebillReminderOpen] = useState(false);
  const [user, setUser] = useState(null);
  // 待识别邮件未读数（导航栏 badge + flash）
  const [emailCount, setEmailCount] = useState(0);
  // 全局阻塞式跟进提醒弹窗
  const [popup, setPopup] = useState(null);
  // 响应式：<768px(手机) 时侧边栏降级为抽屉导航
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [navOpen, setNavOpen] = useState(false);
  // 模块权限配置（从后端 /api/admin/frontend-modules/config 拉取；先用缓存渲染避免菜单闪烁）
  const [modulePerms, setModulePerms] = useState(() => {
    try { return JSON.parse(localStorage.getItem('module_perms') || '[]'); } catch { return []; }
  });

  const fetchEmailCount = async () => {
    try {
      const data = await emailApi.unreadCount();
      setEmailCount(data.count || 0);
    } catch (e) {}
  };

  const fetchRebillReminders = async () => {
    try {
      const d = await rebillApi.getReminders();
      setRebillReminders(d.items || []);
      setRebillReminderCount(d.total_unread || 0);
    } catch (e) {}
  };

  const fetchPopup = async () => {
    try {
      const data = await orderApi.getPopupReminder();
      setPopup(data?.has_reminder ? data : null);
    } catch (e) {}
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchReminders();
    fetchRebillReminders();
    // 每30秒刷新提醒数量
    const timer = setInterval(fetchReminderCount, 30000);
    const rebillTimer = setInterval(fetchRebillReminders, 30000);
    // 待识别邮件数量：初始 + 每60秒轮询 + 路由切换时刷新（已读后数量相应减少）
    fetchEmailCount();
    const emailTimer = setInterval(fetchEmailCount, 60000);
    // 阻塞式弹窗提醒：登录/重新进入系统时重置弹窗队列（不累积离线期间的弹窗次数，
    // 只保留最紧急一条立即可弹，其余按优先级策略从现在重新计时），之后每60秒检查
    orderApi.resetPopupQueue().then(() => fetchPopup()).catch(() => fetchPopup());
    const popupTimer = setInterval(fetchPopup, 60000);
    // 拉取模块权限配置（控制菜单可见性 + 只读模式）
    fetch(`${import.meta.env.VITE_API_URL || ''}/api/admin/frontend-modules/config`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
    })
      .then(r => r.json())
      .then(d => {
        const items = d.items || [];
        setModulePerms(items);
        localStorage.setItem('module_perms', JSON.stringify(items));
      })
      .catch(() => {});
    return () => {
      clearInterval(timer);
      clearInterval(rebillTimer);
      clearInterval(emailTimer);
      clearInterval(popupTimer);
    };
  }, []);

  // 路由变化时刷新邮件未读数（从待识别邮件页返回后数量即时更新）
  useEffect(() => {
    fetchEmailCount();
  }, [location.pathname]);

  // 处理弹窗：取消 = 已看到，从现在按规则重新计时，队列跳下一条（不重复循环）；
  // 详情 = 跳转订单详情，队列剩余提醒延迟5分钟再弹。两者都必须先 ack 才能继续工作。
  const handlePopupAck = async (gotoDetail) => {
    if (!popup) return;
    const { reminder_id, order_id } = popup;
    try {
      await orderApi.ackPopupReminder(reminder_id, gotoDetail ? 'detail' : 'cancel');
    } catch (e) {}
    setPopup(null);
    if (gotoDetail && order_id) {
      navigateToOrderDetail(navigate, order_id);
    }
    fetchPopup();
  };

  // 弹窗免打扰（按账号隔离，只影响自己）：
  // 延迟10/30分钟、今日不再显示=本账号全部弹窗；仅本弹窗今日不再显示=只屏蔽当前这一条
  const SNOOZE_ITEMS = [
    { key: '10m', label: '延迟所有弹窗10分钟' },
    { key: '30m', label: '延迟所有弹窗30分钟' },
    { key: 'today', label: '今日不再显示所有弹窗' },
    { key: 'this_today', label: '仅本弹窗今日不再显示' },
  ];
  const handleSnooze = async ({ key, domEvent }) => {
    domEvent?.stopPropagation?.();
    const item = SNOOZE_ITEMS.find((i) => i.key === key);
    try {
      await orderApi.snoozePopup(key, key === 'this_today' ? popup?.reminder_id : undefined);
      message.success(`已设置「${item.label}」，到期自动恢复提醒`);
      setPopup(null);
      fetchPopup();
    } catch (e) {
      message.error(e.message || '设置失败');
    }
  };

  const handleEscalate = async () => {
    if (!popup) return;
    try {
      await orderApi.escalate(popup.order_id);
      message.success('已升级为紧急重要，按紧急订单规则运行');
    } catch (e) {}
    setPopup(null);
    fetchPopup();
  };

  const handleDismissEscalation = async () => {
    if (!popup) return;
    try {
      await orderApi.dismissEscalation(popup.order_id);
      message.success('本次不升级，提醒间隔恢复原规则');
    } catch (e) {}
    setPopup(null);
    fetchPopup();
  };

  const fetchReminders = async () => {
    try {
      const data = await orderApi.getReminders();
      setReminders(data);
      setReminderCount(data.length);
    } catch (e) {}
  };

  const fetchReminderCount = async () => {
    try {
      const data = await orderApi.getReminderCount();
      setReminderCount(data.count);
    } catch (e) {}
  };

  const handleMarkAllRead = async () => {
    await orderApi.markAllRead();
    setReminderCount(0);
    setReminders([]);
    message.success('已全部标记为已读');
  };

  // 点击提醒单条：标记已读（数字-1）并跳转工单详情
  const handleReminderClick = async (item) => {
    try { await orderApi.markReminderRead(item.id); } catch (e) {}
    setReminders((prev) => prev.filter((r) => r.id !== item.id));
    setReminderCount((c) => Math.max(0, c - 1));
    sessionStorage.setItem('order_detail_back', location.pathname + location.search);
    navigate(`/orders/detail/${item.order_id}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: `${user?.full_name || user?.username || '用户'} (${user?.role || ''})`, disabled: true },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
    ],
  };

  // 模块权限查询：路径→module_key 由后端下发的模块配置动态构建（含二级子路由、前缀兜底）
  const pathToKey = buildPathToKey(modulePerms);
  const permMap = {};
  modulePerms.forEach(m => { permMap[m.module_key] = m.access_level || 'rw'; });
  const resolveKey = (p) => {
    if (pathToKey[p]) return pathToKey[p];
    const hit = Object.keys(pathToKey).filter((k) => p.startsWith(k + '/')).sort((a, b) => b.length - a.length)[0];
    return hit ? pathToKey[hit] : undefined;
  };

  // 菜单动态生成：一级菜单完全由后端模块配置（label/icon/path/排序/权限过滤）驱动；
  // 前端只补充图标组件和二级子路由。后续一级路由增减：只需改后端 DEFAULT_MODULE_SEED + App.js 路由
  const menuItems = modulePerms.map((m) => {
    const children = MENU_CHILDREN[m.module_key];
    const item = {
      // 有子路由的分组用唯一 group key（不与子项路径重复）；无子路由直接用模块 path
      key: children ? `/${m.module_key}-group` : (m.path || m.module_key),
      icon: MENU_ICON_MAP[m.icon] || <FileTextOutlined />,
      label: m.module_key === 'emails'
        ? (
          <Badge count={emailCount} size="small" className={emailCount > 0 ? 'badge-flash' : ''} offset={[14, -2]}>
            <span>{m.label}</span>
          </Badge>
        )
        : m.label,
    };
    if (children) item.children = children;
    return item;
  }).filter(item => {
    const mk = resolveKey(item.key);
    return !mk || permMap[mk] !== 'disabled';
  });

  // 导航菜单：桌面端渲染在固定 Sider，手机端渲染在抽屉 Drawer（点击后自动收起）
  const navMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      defaultOpenKeys={['/my-orders-group', '/orders-group', '/appeals-group', '/claims-group', '/customers-group']}
      items={menuItems}
      onClick={({ key }) => { navigate(key); setNavOpen(false); }}
      style={{ borderRight: 0, marginTop: 8 }}
    />
  );

  const reminderMenu = {
    items: [
      {
        key: 'list',
        label: (
          <div style={{ width: respWidth(320), maxHeight: 400, overflow: 'auto' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>订单提醒 ({reminderCount})</Text>
              <Button type="link" size="small" icon={<CheckOutlined />} onClick={handleMarkAllRead}>全部已读</Button>
            </div>
            <List
              dataSource={reminders.slice(0, 10)}
              locale={{ emptyText: '暂无提醒' }}
              renderItem={(item) => (
                <List.Item
                  className="reminder-list-item"
                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                  onClick={() => handleReminderClick(item)}
                >                  <List.Item.Meta
                    title={<Text strong>{item.order_no}</Text>}
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.customer_name}</Text>
                        <div style={{ fontSize: 12, marginTop: 2 }}>{item.message}</div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        ),
      },
    ],
  };

  const handleMarkAllRebillRead = async () => {
    try {
      await rebillApi.markAllRemindersRead();
      setRebillReminderCount(0);
      setRebillReminders([]);
      message.success('返账单提醒已全部标记为已读');
    } catch (e) { message.error(e.message || '操作失败'); }
  };

  const handleRebillReminderClick = async (item) => {
    // 只有未读才调后端 + 扣角标（避免重复点击也重复扣）
    if (!item.is_read) {
      try { await rebillApi.markReminderRead(item.id); } catch (e) {
        // 标已读失败 → 不扣角标、不本地改 is_read，给用户错误提示
        message.error(e.message || '标记已读失败，请稍后重试');
        return;
      }
      setRebillReminders((prev) => prev.map((x) => x.id === item.id ? { ...x, is_read: true } : x));
      setRebillReminderCount((n) => Math.max(0, n - 1));
    }
    navigate(`/rebills?highlight=${item.rebill_id}`);
  };

  // 新提醒到达时弹出 notification：支持
  //   (a) 首次登录已有未读 → 0→N 要弹
  //   (b) 0→1 要弹
  //   (c) 列表满额(=10)后新提醒靠 id 判断要弹（不再仅靠长度比较）
  const prevReminderIdsRef = React.useRef(new Set());
  useEffect(() => {
    const cur = (rebillReminders || []).filter((r) => !r.is_read);
    const curIds = new Set(cur.map((r) => r.id));
    const prevIds = prevReminderIdsRef.current;

    // 算出"新增的未读 id"集合（prev 为空 → 登录首次刷新场景，全部视为 new）
    const firstLoad = prevIds.size === 0 && curIds.size > 0;
    let newIds;
    if (firstLoad) {
      newIds = curIds;          // 登录已有未读 → 也算新的，要弹
    } else {
      newIds = new Set();
      for (const id of curIds) {
        if (!prevIds.has(id)) newIds.add(id);
      }
    }

    // 找一条新的未读 → 弹 notification（避免连续刷屏）
    if (newIds.size > 0) {
      const firstNew = cur.find((r) => newIds.has(r.id));
      if (firstNew) {
        notification.info({
          message: `返账单提醒：${firstNew.rebill_no || `#${firstNew.rebill_id}`}`,
          description: firstNew.message,
          placement: 'topRight',
          duration: 8,
          onClick: () => handleRebillReminderClick(firstNew),
        });
      }
    }

    prevReminderIdsRef.current = curIds;
  }, [rebillReminderCount, rebillReminders]);

  const rebillReminderMenu = {
    items: [
      {
        key: 'rebill-list',
        label: (
          <div style={{ width: respWidth(340), maxHeight: 400, overflow: 'auto' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>返账单提醒 ({rebillReminderCount})</Text>
              <Button type="link" size="small" icon={<CheckOutlined />} onClick={handleMarkAllRebillRead}>全部已读</Button>
            </div>
            <List
              dataSource={rebillReminders.slice(0, 10)}
              locale={{ emptyText: '暂无返账单提醒' }}
              renderItem={(item) => (
                <List.Item
                  className="reminder-list-item"
                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                  onClick={() => handleRebillReminderClick(item)}
                >
                  <List.Item.Meta
                    title={<Text strong>{item.rebill_no || `#${item.rebill_id}`}</Text>}
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.work_type === 'escalated' ? '升级提醒' : '超期提醒'}</Text>
                        {item.rebill_amount != null && (
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>金额 ¥{item.rebill_amount}</Text>
                        )}
                        <div style={{ fontSize: 12, marginTop: 2 }}>{item.message}</div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        ),
      },
    ],
  };

  const logoBlock = (
    <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 15, fontWeight: 500, letterSpacing: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      调度管理系统
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端：固定深色侧边栏 */}
      {!isMobile && (
        <Sider width={220} theme="dark" style={{ position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100 }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {logoBlock}
            <div className="sidebar-menu-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              {navMenu}
            </div>
          </div>
        </Sider>
      )}
      {/* 手机端：侧边栏降级为抽屉导航，默认隐藏，点左上角菜单按钮呼出 */}
      {isMobile && (
        <Drawer
          placement="left"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          width={respWidth(260)}
          closable={false}
          title={<span style={{ color: '#fff', fontWeight: 500 }}>调度管理系统</span>}
          styles={{
            header: { background: '#171A20', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 16px' },
            body: { background: '#171A20', padding: 0 },
          }}
        >
          {navMenu}
        </Drawer>
      )}
      <Layout style={{ marginLeft: isMobile ? 0 : 220 }}>
        <Header style={{ background: '#fff', padding: isMobile ? '0 12px' : '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: 'none', borderBottom: '1px solid #EEEEEE', position: 'sticky', top: 0, zIndex: 99 }}>
          {/* 手机端左上角菜单按钮；桌面端留空（右侧操作区） */}
          <div>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined style={{ fontSize: 20 }} />} onClick={() => setNavOpen(true)} aria-label="打开菜单" />
            )}
          </div>
          <Space size={isMobile ? 12 : 24}>
            <OrderDetailPortal />
            <Dropdown menu={reminderMenu} trigger={['click']}>
              <Badge
                count={reminderCount}
                size="small"
                className={reminderCount > 0 ? 'badge-flash' : ''}
              >
                <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
              </Badge>
            </Dropdown>
            <Dropdown menu={rebillReminderMenu} trigger={['click']}>
              <Badge
                count={rebillReminderCount}
                size="small"
                className={rebillReminderCount > 0 ? 'badge-flash' : ''}
              >
                <Button type="text" icon={<FileTextOutlined style={{ fontSize: 18 }} />} />
              </Badge>
            </Dropdown>
            <Dropdown menu={userMenu} trigger={['click']}>
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} style={{ background: '#3E6AE1' }} />
                {!isMobile && <Text>{user?.full_name || user?.username || '用户'}</Text>}
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: isMobile ? '12px' : '24px', minHeight: 'calc(100vh - 112px)' }}>
          <Outlet />
        </Content>
      </Layout>

      {/* 全局阻塞式跟进提醒弹窗：不点「详情」或「取消」无法继续在页面工作 */}
      <Modal
        open={!!popup}
        title={popup ? (
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>{popup.work_type === 'appeal' ? '申诉工单跟进提醒' : (popup.target_role === 'support' ? '待客服处理工单提醒' : '调度工单跟进提醒')}</span>
            <Tag color={priorityColor[popup.priority]}>{priorityText[popup.priority] || '普通'}</Tag>
          </Space>
        ) : null}
        onCancel={() => handlePopupAck(false)}
        maskClosable={false}
        keyboard={false}
        closable={false}
        zIndex={3000}
        footer={[
          <Dropdown
            key="snooze"
            trigger={['click']}
            menu={{ items: SNOOZE_ITEMS.map((i) => ({ key: i.key, label: i.label })), onClick: handleSnooze }}
          >
            <Button icon={<ClockCircleOutlined />}>免打扰</Button>
          </Dropdown>,
          <span key="spacer" style={{ flex: 1 }} />,
          <Button key="cancel" onClick={() => handlePopupAck(false)}>取消</Button>,
          <Button key="detail" type="primary" onClick={() => handlePopupAck(true)}>详情</Button>,
        ]}
      >
        {popup && (
          <div>
            {popup.suggest_upgrade && (
              <div style={{
                background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                padding: '12px 16px', marginBottom: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <span style={{ color: '#ff4d4f', fontSize: 22, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  建议升级
                </span>
                <Space>
                  <Button onClick={handleDismissEscalation}>不升级</Button>
                  <Button type="primary" danger onClick={handleEscalate}>升级</Button>
                </Space>
              </div>
            )}
            <p style={{ marginBottom: 8 }}>
              <Text strong>{popup.order_no}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>{popup.customer_name}</Text>
            </p>
            {popup.core_issue && (
              <p style={{ marginBottom: 8 }}><Text type="secondary">核心问题：</Text>{renderLatestSegment(popup.core_issue)}</p>
            )}
            <p style={{ marginBottom: 8 }}>{popup.message}</p>
            <p style={{ marginBottom: 0, fontSize: 12, color: '#999' }}>
              跟进要求：{popup.follow_up_text || '按策略跟进'}；提醒间隔：每 {popup.reminder_hours} 小时（系统按优先级固定，不可更改）
              {popup.suggest_upgrade && '；该异常单已6小时无操作/邮件，间隔临时升级为2小时'}
            </p>
          </div>
        )}
      </Modal>
    </Layout>
  );
}
